import { Dialog, Transition } from '@headlessui/react';
import { Download, X, ZoomIn, ZoomOut } from 'lucide-react';

import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { toast } from '@/utils/toast';

// Extract base URL without query params for cache key (handles presigned URLs)
function getImageCacheKey(url: string): string {
  try {
    const urlObj = new URL(url);
    return `${urlObj.origin}${urlObj.pathname}`;
  } catch {
    return url;
  }
}

/**
 * Get file extension from MIME type
 */
function getExtensionFromMimeType(mimeType: string): string {
  const mimeToExt: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'image/svg+xml': 'svg',
    'image/bmp': 'bmp',
    'image/tiff': 'tiff',
    'image/heic': 'heic',
    'image/heif': 'heif',
    'image/avif': 'avif',
  };
  return mimeToExt[mimeType] || 'jpg';
}

interface PrefetchedImage {
  blobUrl: string;
  extension: string;
}

// Module-level cache for prefetched image blobs (persists across modal open/close)
const blobCache = new Map<string, PrefetchedImage>();

/**
 * Pre-fetches an image and returns a blob URL for downloading.
 * Uses module-level cache to avoid re-fetching the same image.
 */
async function prefetchImageBlob(
  imageUrl: string
): Promise<PrefetchedImage | null> {
  const cacheKey = getImageCacheKey(imageUrl);

  // Return cached blob if available
  const cached = blobCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return null;
    const blob = await response.blob();
    const result: PrefetchedImage = {
      blobUrl: window.URL.createObjectURL(blob),
      extension: getExtensionFromMimeType(blob.type),
    };

    // Cache the blob URL for future use
    blobCache.set(cacheKey, result);

    return result;
  } catch {
    return null;
  }
}

interface ImageViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Full-size original image URL for viewing */
  imageUrl: string;
  /** Optional title for the modal header */
  title?: string;
  /** Optional filename for download (without extension) */
  downloadFilename?: string;
}

/**
 * Full-screen modal for viewing images.
 * Features:
 * - Pinch-to-zoom on mobile
 * - Zoom controls on desktop
 * - Download button
 * - Close button
 */
export function ImageViewerModal({
  isOpen,
  onClose,
  imageUrl,
  title,
  downloadFilename,
}: ImageViewerModalProps) {
  const { t } = useTranslation();
  useNamespace('common');

  // Get cache key for the current image (strips query params from presigned URLs)
  const imageCacheKey = useMemo(() => getImageCacheKey(imageUrl), [imageUrl]);

  // Check if this image blob has been cached (avoids re-fetch and shows instant)
  const isImageCached = useMemo(
    () => blobCache.has(imageCacheKey),
    [imageCacheKey]
  );

  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  // Track which image's <img> tag has finished loading
  const [loadedImageKey, setLoadedImageKey] = useState<string | null>(null);
  const [prefetchedImage, setPrefetchedImage] =
    useState<PrefetchedImage | null>(null);

  // Show spinner if image is not cached AND hasn't finished loading yet
  const showLoadingSpinner = !isImageCached && loadedImageKey !== imageCacheKey;

  // Pre-fetch the image blob when modal opens for reliable downloads
  useEffect(() => {
    if (!isOpen || !imageUrl) {
      return;
    }

    let cancelled = false;

    prefetchImageBlob(imageUrl).then(result => {
      if (!cancelled && result) {
        setPrefetchedImage(result);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, imageUrl]);

  // Reset state when modal opens/closes
  const handleClose = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    // Don't revoke blob URL - it's cached at module level for reuse
    // Just clear local state, the cached blob remains available
    setPrefetchedImage(null);
    onClose();
  }, [onClose]);

  const handleImageLoad = useCallback(() => {
    // Mark this image as loaded so spinner hides
    setLoadedImageKey(imageCacheKey);
  }, [imageCacheKey]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.5, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => {
      const newZoom = Math.max(prev - 0.5, 1);
      // Reset position when zooming back to 1
      if (newZoom === 1) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const handleResetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Mouse/touch drag for panning when zoomed
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.clientX - position.x,
          y: e.clientY - position.y,
        });
      }
    },
    [zoom, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoom > 1) {
        setPosition({
          x: e.clientX - dragStart.x,
          y: e.clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart, zoom]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Touch events for mobile pinch-to-zoom
  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1 && zoom > 1) {
        setIsDragging(true);
        setDragStart({
          x: e.touches[0].clientX - position.x,
          y: e.touches[0].clientY - position.y,
        });
      }
    },
    [zoom, position]
  );

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (e.touches.length === 1 && isDragging && zoom > 1) {
        setPosition({
          x: e.touches[0].clientX - dragStart.x,
          y: e.touches[0].clientY - dragStart.y,
        });
      }
    },
    [isDragging, dragStart, zoom]
  );

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Double-click/tap to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (zoom === 1) {
      setZoom(2);
    } else {
      handleResetZoom();
    }
  }, [zoom, handleResetZoom]);

  // Download handler - uses pre-fetched blob for reliable downloads
  const handleDownload = useCallback(() => {
    const filename = downloadFilename || `image-${Date.now()}`;

    // Use pre-fetched blob URL for synchronous download (preserves user gesture)
    if (prefetchedImage) {
      const link = document.createElement('a');
      link.href = prefetchedImage.blobUrl;
      link.download = `${filename}.${prefetchedImage.extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      return;
    }

    // Fallback: open image in new tab if blob not ready
    // This lets the browser handle the download natively
    const newWindow = window.open(imageUrl, '_blank');
    if (!newWindow) {
      toast.error(t('common:imageViewer.downloadError'));
    }
  }, [prefetchedImage, imageUrl, downloadFilename, t]);

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/90" />
        </Transition.Child>

        <div className="fixed inset-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full h-full flex flex-col">
              {/* Header with controls */}
              <div className="flex items-center justify-between p-4 bg-black/50">
                <Dialog.Title className="text-white font-medium">
                  {title || t('common:imageViewer.title')}
                </Dialog.Title>

                <div className="flex items-center gap-2">
                  {/* Zoom controls */}
                  <div className="hidden sm:flex items-center gap-1 mr-2">
                    <button
                      onClick={handleZoomOut}
                      disabled={zoom <= 1}
                      className="p-2 rounded-full text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label={t('common:upload.zoomOut')}
                    >
                      <ZoomOut className="h-5 w-5" />
                    </button>

                    <button
                      onClick={handleResetZoom}
                      className="px-2 py-1 rounded text-white text-sm hover:bg-white/20 transition-colors min-w-[60px]"
                    >
                      {Math.round(zoom * 100)}%
                    </button>

                    <button
                      onClick={handleZoomIn}
                      disabled={zoom >= 4}
                      className="p-2 rounded-full text-white hover:bg-white/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      aria-label={t('common:upload.zoomIn')}
                    >
                      <ZoomIn className="h-5 w-5" />
                    </button>
                  </div>

                  {/* Download button - hidden on mobile/tablet due to poor UX */}
                  <button
                    onClick={handleDownload}
                    className="hidden md:block p-2 rounded-full text-white hover:bg-white/20 transition-colors"
                    aria-label={t('common:imageViewer.download')}
                    title={t('common:imageViewer.download')}
                  >
                    <Download className="h-5 w-5" />
                  </button>

                  {/* Close button */}
                  <button
                    onClick={handleClose}
                    className="p-2 rounded-full text-white hover:bg-white/20 transition-colors"
                    aria-label={t('common:actions.close')}
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Image container */}
              <div
                className="flex-1 overflow-hidden flex items-center justify-center cursor-grab active:cursor-grabbing"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onDoubleClick={handleDoubleClick}
              >
                {/* Loading spinner - hidden if image is in cache */}
                {showLoadingSpinner && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                  </div>
                )}
                <img
                  src={imageUrl}
                  alt={title || t('common:imageViewer.imageAlt')}
                  className={`max-w-full max-h-full object-contain select-none transition-opacity duration-300 ${
                    showLoadingSpinner ? 'opacity-0' : 'opacity-100'
                  }`}
                  style={{
                    transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out',
                  }}
                  draggable={false}
                  onLoad={handleImageLoad}
                />
              </div>

              {/* Mobile zoom hint */}
              <div className="sm:hidden p-3 text-center text-white/70 text-sm bg-black/50">
                {t('common:imageViewer.zoomHint')}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
